import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../axiosInstance";

// Utility to safely get current user ID
const getCurrentUserId = () => {
  try {
    const stored = localStorage.getItem("userId");
    if (!stored) return null;
    return stored.startsWith("{") ? JSON.parse(stored)._id : stored;
  } catch (err) {
    console.error("Failed to parse userId from localStorage:", err);
    return null;
  }
};

const initialState = {
  chatList: [],
  selectedChatId: null,
  mobileView: "both",
  loading: false,
  error: null,
};

// ====================== Thunks ====================== //

export const fetchChatList = createAsyncThunk(
  "sellerChat/fetchChatList",
  async (_, { rejectWithValue }) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      };
      const response = await axios.get("/api/v1/chat/list", config);
      const chatsMap = new Map();
      response.data.data.forEach((chat) => {
        const chatId = chat._id?.toString();
        if (!chatId)
          return console.warn("Skipping chat with undefined ID:", chat);

        const existingChat = chatsMap.get(chatId);
        const chatTime = chat.time ? new Date(chat.time).getTime() : 0;
        const existingTime = existingChat
          ? new Date(existingChat.time).getTime()
          : 0;

        if (!existingChat || chatTime > existingTime) {
          chatsMap.set(chatId, {
            id: chatId,
            name: chat.name || "Unknown User",
            email: chat.email || "",
            phone: chat.phone || "",
            lastMessage: chat.lastMessage || "",
            time: chat.time || new Date().toISOString(),
            unreadCount: chat.unreadCount || 0,
            messages: [],
          });
        }
      });

      const chats = Array.from(chatsMap.values()).sort(
        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
      );
      return chats;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch chat list"
      );
    }
  }
);

// export const fetchChatHistory = createAsyncThunk(
//   "sellerChat/fetchChatHistory",
//   async (userId, { rejectWithValue }) => {
//     try {
//       const config = {
//         headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
//       };
//       const response = await axios.get(
//         `/api/v1/chat/history/${userId}`,
//         config
//       );
//       const currentUserId = getCurrentUserId();
//       const messages = response.data.data.map((msg) => ({
//         _id: msg._id?.toString(),
//         message: msg.message,
//         text: msg.message,
//         fromMe: msg.sender._id.toString() === currentUserId,
//         createdAt: msg.createdAt || new Date().toISOString(),
//         isRead: msg.isRead || false,
//         sender: msg.sender,
//         receiver: msg.receiver,
//       }));

//       const unreadCount = messages.filter(
//         (msg) => !msg.fromMe && !msg.isRead
//       ).length;
//       return { userId, messages, unreadCount };
//     } catch (error) {
//       return rejectWithValue(
//         error.response?.data?.message || "Failed to fetch chat history"
//       );
//     }
//   }
// );

export const fetchChatHistory = createAsyncThunk(
  "sellerChat/fetchChatHistory",
  async (userId, { rejectWithValue }) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      };
      const response = await axios.get(
        `/api/v1/chat/history/${userId}`,
        config
      );
      
      const currentUserId = getCurrentUserId(); // ✅ सही यूज़र ID निकालें
      
      const messages = response.data.data.map((msg) => ({
        _id: msg._id?.toString(),
        message: msg.message,
        text: msg.message,
        // ✅ यहाँ महत्वपूर्ण सुधार:
        fromMe: msg.sender._id?.toString() === currentUserId,
        createdAt: msg.createdAt || new Date().toISOString(),
        isRead: msg.isRead || false,
        sender: msg.sender,
        receiver: msg.receiver,
      }));

      const unreadCount = messages.filter(
        (msg) => !msg.fromMe && !msg.isRead
      ).length;
      return { userId, messages, unreadCount };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch chat history"
      );
    }
  }
);
export const sendMessage = createAsyncThunk(
  "sellerChat/sendMessage",
  async ({ receiverId, message }, { rejectWithValue }) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      };
      const response = await axios.post(
        "/api/v1/chat",
        { receiverId, message },
        config
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send message"
      );
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  "sellerChat/markMessagesAsRead",
  async (userId, { rejectWithValue }) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      };
      await axios.put(`/api/v1/chat/read/${userId}`, {}, config);
      return userId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark messages as read"
      );
    }
  }
);

// ====================== Slice ====================== //

const sellerChatSlice = createSlice({
  name: "sellerChat",
  initialState,
  reducers: {
    setSelectedChatId(state, action) {
      state.selectedChatId = action.payload;
    },

    setMobileView(state, action) {
      state.mobileView = action.payload;
    },
    addMessage: (state, action) => {
      const msg = action.payload;
      const chatIndex = state.chatList.findIndex(
        (chat) => chat.id === msg.sender._id || chat.id === msg.receiver._id
      );

      if (chatIndex !== -1) {
        state.chatList[chatIndex].messages = [
          ...(state.chatList[chatIndex].messages || []),
          msg,
        ];
        // update lastMessage and time
        state.chatList[chatIndex].lastMessage = msg.message || msg.text;
        state.chatList[chatIndex].time = msg.createdAt;
        // unread count
        if (msg.sender._id !== state.currentUserId) {
          state.chatList[chatIndex].unreadCount =
            (state.chatList[chatIndex].unreadCount || 0) + 1;
        }
      } else {
        // new chat
        state.chatList.push({
          id:
            msg.sender._id === state.currentUserId
              ? msg.receiver._id
              : msg.sender._id,
          name:
            msg.sender._id === state.currentUserId
              ? msg.receiver.name
              : msg.sender.name,
          initials:
            msg.sender._id === state.currentUserId
              ? msg.receiver.name[0]
              : msg.sender.name[0],
          messages: [msg],
          lastMessage: msg.message || msg.text,
          time: msg.createdAt,
          unreadCount: msg.sender._id !== state.currentUserId ? 1 : 0,
        });
      }
    },

    markChatAsRead(state, action) {
      const chatId = action.payload;
      const chat = state.chatList.find((c) => c.id === chatId);

      if (chat) {
        if (chat.messages) chat.messages.forEach((msg) => (msg.isRead = true));
        chat.unreadCount = 0;
        console.log("All messages marked as read for chat:", chatId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatList.fulfilled, (state, action) => {
        state.loading = false;
        state.chatList = action.payload.map((chat) => ({
          ...chat,
          initials: chat.name
            ? chat.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()
            : "UN",
          messages: chat.messages || [],
          unreadCount: chat.unreadCount || 0,
        }));
      })
      .addCase(fetchChatList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // .addCase(fetchChatHistory.fulfilled, (state, action) => {
      //   state.loading = false;
      //   const { userId, messages, unreadCount } = action.payload;

      //   let chat = state.chatList.find((c) => c.id === userId);

      //   if (chat) {
      //     chat.messages = messages;
      //     chat.unreadCount = unreadCount;
      //     if (messages.length > 0) {
      //       const latestMessage = messages[messages.length - 1];
      //       chat.lastMessage = latestMessage.message || latestMessage.text;
      //       chat.time = latestMessage.createdAt;
      //     }
      //   } 
      //   else {
      //     state.chatList.push({
      //       id: userId,
      //       name: "Unknown User",
      //       lastMessage:
      //         messages.length > 0 ? messages[messages.length - 1].message : "",
      //       time:
      //         messages.length > 0
      //           ? messages[messages.length - 1].createdAt
      //           : new Date().toISOString(),
      //       unreadCount,
      //       messages,
      //       initials: "UN",
      //     });
      //   }
      // })
      // fetchChatHistory.fulfilled में समस्या:
.addCase(fetchChatHistory.fulfilled, (state, action) => {
  state.loading = false;
  const { userId, messages, unreadCount } = action.payload;

  let chat = state.chatList.find((c) => c.id === userId);

  if (chat) {
    chat.messages = messages;
    chat.unreadCount = unreadCount;
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      chat.lastMessage = latestMessage.message || latestMessage.text;
      chat.time = latestMessage.createdAt;
      // ✅ नाम भी अपडेट करें
      if (!chat.name || chat.name === "Unknown User") {
        chat.name = latestMessage.sender?.name || latestMessage.receiver?.name || chat.name;
      }
    }
  } else {
    // ✅ नया चैट बनाते समय नाम सेट करें
    const chatName = messages.length > 0 
      ? (messages[0].sender?._id === getCurrentUserId() 
          ? messages[0].receiver?.name 
          : messages[0].sender?.name)
      : "Unknown User";

    state.chatList.push({
      id: userId,
      name: chatName || "Unknown User",
      email: messages.length > 0 
        ? (messages[0].sender?._id === getCurrentUserId()
            ? messages[0].receiver?.email
            : messages[0].sender?.email)
        : "",
      lastMessage: messages.length > 0 ? messages[messages.length - 1].message : "",
      time: messages.length > 0
        ? messages[messages.length - 1].createdAt
        : new Date().toISOString(),
      unreadCount,
      messages,
      initials: chatName
        ? chatName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()
        : "UN",
    });
  }
})
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        const message = action.payload;
        const currentUserId = getCurrentUserId();

        const chatUserId = message.receiver._id;
        let chat = state.chatList.find((c) => c.id === chatUserId);

        if (chat) {
          if (!chat.messages) chat.messages = [];

          const messageExists = chat.messages.some(
            (msg) => msg._id === message._id
          );
          if (!messageExists) {
            chat.messages.push({
              _id: message._id,
              message: message.message,
              text: message.message,
              fromMe: true,
              createdAt: message.createdAt,
              isRead: true,
              sender: message.sender,
              receiver: message.receiver,
            });
            chat.lastMessage = message.message;
            chat.time = message.createdAt;
            chat.unreadCount = 0;
          }
        } else {
          state.chatList.unshift({
            id: chatUserId,
            name: message.receiver.name,
            lastMessage: message.message,
            time: message.createdAt,
            unreadCount: 0,
            messages: [
              {
                _id: message._id,
                message: message.message,
                text: message.message,
                fromMe: true,
                createdAt: message.createdAt,
                isRead: true,
                sender: message.sender,
                receiver: message.receiver,
              },
            ],
            initials: message.receiver.name
              ? message.receiver.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()
              : "UN",
          });
        }

        state.chatList.sort(
          (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
        );
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const chatId = action.payload;
        const chat = state.chatList.find((c) => c.id === chatId);

        if (chat) {
          if (chat.messages)
            chat.messages.forEach((msg) => {
              if (!msg.fromMe) msg.isRead = true;
            });
          chat.unreadCount = 0;
        }
      });
  },
});

export const { setSelectedChatId, setMobileView, addMessage, markChatAsRead } =
  sellerChatSlice.actions;
export default sellerChatSlice.reducer;
